import {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
} from 'react'

import { Grid } from '@material-ui/core'
import * as Yup from 'yup'
import { MdPlayArrow } from 'react-icons/md'

import SnackBar from 'shared/components/SnackBar'
import { cepMask } from 'shared/utils/formatters'
import dimensions from 'shared/styles/dimensions'
import Scaffold from 'shared/components/ScaffoldNew'
import { colors } from 'shared/styles/colors'

import { useMotoristas, IAddress } from '../../../../hooks/useMotoristas'
import { IAddressComponentsProps } from './AddressComponent.types'
import {
  Container,
  Title,
  StyledInput,
  StyledForm,
  StyledFormFullAddress,
} from './styles'

const Address: FunctionComponent<IAddressComponentsProps> = ({
  consultZipCode,
}) => {
  const {
    motoristas,
    setMotoristas,
    handleBackButton,
    handleNextButton,
    loading,
  } = useMotoristas()
  const [formOk, setFormOk] = useState(false)
  const [errorMessageSnackBar, setErrorMessageSnackBar] = useState('')
  const [showingError, setShowingError] = useState(false)
  const [viewFullAddress, setViewFullAddress] = useState(false)
  const [address, setAddress] = useState<IAddress>(() => {
    if (motoristas && motoristas.endereco) {
      return motoristas.endereco
    }

    return {} as IAddress
  })

  useEffect(() => {
    if (motoristas && motoristas.endereco && motoristas.endereco.numero) {
      setFormOk(true)
      setViewFullAddress(true)
    }
  }, [motoristas, setFormOk])

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setFormOk(false)

      if (!e.target.value) {
        setMotoristas({ endereco: null })
        setViewFullAddress(false)

        return
      }

      try {
        const schema = Yup.object().shape({
          cep: Yup.string().matches(/^([\d]{5})-*([\d]{3})/),
        })

        await schema.validate(
          {
            cep: e.target.value,
          },
          {
            abortEarly: false,
          },
        )

        const response = await consultZipCode.execute({ cep: e.target.value })

        if (response.cidade) {
          setMotoristas({ endereco: { cep: e.target.value, ...response } })
          setAddress({ ...response })
          setFormOk(true)
          setViewFullAddress(true)
        } else {
          setMotoristas({ endereco: null })
          setViewFullAddress(false)
          setShowingError(true)
          setErrorMessageSnackBar('Cep inválido.')
        }
      } catch (e) {
        setFormOk(false)
      }
    },
    [setMotoristas, setFormOk, consultZipCode],
  )

  const handleNextButtonFn = async () => {
    try {
      const schema = Yup.object().shape({
        cep: Yup.string().matches(/^([\d]{5})-*([\d]{3})/),
        address: Yup.string().required('Logradouro inválido!'),
        number: Yup.string().required('Número inválido!'),
        neighborhood: Yup.string().required('Bairro inválido!'),
        city: Yup.string().required('Cidade inválido!'),
        state: Yup.string().required('Estado inválido!'),
        complement: Yup.string().optional(),
      })
      await schema.validate(
        {
          cep: motoristas.endereco.cep,
          neighborhood: motoristas.endereco.bairro,
          city: motoristas.endereco.cidade,
          state: motoristas.endereco.estado,
          address: motoristas.endereco.logradouro,
          number: motoristas.endereco.numero,
          complement: motoristas.endereco.complemento,
        },
        {
          abortEarly: false,
        },
      )
      handleNextButton()
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessageSnackBar(error.message)
      } else {
        setErrorMessageSnackBar('Erro ao validar os dados do endereço')
      }
      setShowingError(true)
      setFormOk(false)
    }
  }
  return (
    <Scaffold
      showFooterScaffold
      titleBackButton="Voltar"
      onClickBackButton={handleBackButton}
      icon={MdPlayArrow}
      titleNextButton="Próximo"
      onClickNextButton={handleNextButtonFn}
      isNextButtonDisabled={!formOk}
      color={formOk ? colors.green : colors.gray}
      loading={loading}
      titleHeader="Novo motorista"
    >
      <Container>
        <Title>Endereço</Title>

        <StyledForm
          onSubmit={undefined}
          initialValues={{
            cep: address && address.cep ? address.cep : '',
          }}
          validationSchema={() => ({
            cep: Yup.string().required('Telefone inválido!'),
          })}
        >
          {formControl => (
            <Grid
              container
              spacing={2}
              style={{ marginBottom: dimensions.spaces.small }}
            >
              <Grid container item xs={12} spacing={4}>
                <Grid item xs={6}>
                  <StyledInput
                    label="Qual o endereço do motorista?"
                    placeholder="Digite o CEP"
                    name="cep"
                    type="text"
                    formControl={formControl}
                    mask={value => cepMask(value)}
                    onChange={handleChange}
                    maxLength={9}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </StyledForm>
        {viewFullAddress && (
          <StyledFormFullAddress
            onSubmit={undefined}
            initialValues={{
              address: address && address.logradouro ? address.logradouro : '',
              number: address && address.numero ? address.numero : '',
              neighborhood: address && address.bairro ? address.bairro : '',
              city: address && address.cidade ? address.cidade : '',
              state: address && address.estado ? address.estado : '',
              complement:
                address && address.complemento ? address.complemento : '',
            }}
            validationSchema={() => ({
              address: Yup.string().required('Logradouro inválido!'),
              number: Yup.string().required('Número inválido!'),
              neighborhood: Yup.string().required('Bairro inválido!'),
              city: Yup.string().required('Cidade inválido!'),
              state: Yup.string().required('Estado inválido!'),
              complement: Yup.string().optional(),
            })}
          >
            {formControl => (
              <Grid container spacing={2}>
                <Grid container item xs={12} spacing={4}>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Logradouro"
                      placeholder="Informe a rua"
                      name="address"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotoristas({
                          endereco: {
                            ...motoristas.endereco,
                            logradouro: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Número"
                      placeholder="Informe o número"
                      name="number"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotoristas({
                          endereco: {
                            ...motoristas.endereco,
                            numero: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={4}>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Complemento"
                      placeholder="Opcional"
                      name="complement"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotoristas({
                          endereco: {
                            ...motoristas.endereco,
                            complemento: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Bairro"
                      placeholder="Informe o bairro"
                      name="neighborhood"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotoristas({
                          endereco: {
                            ...motoristas.endereco,
                            bairro: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={4}>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Cidade"
                      placeholder="Informe a cidade"
                      name="city"
                      formControl={formControl}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledInput
                      label="Estado"
                      placeholder="Informe o estado"
                      name="state"
                      formControl={formControl}
                      disabled={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </StyledFormFullAddress>
        )}
        <SnackBar
          open={showingError}
          setOpen={setShowingError}
          severity="error"
          message={errorMessageSnackBar}
          horizontal="right"
        />
      </Container>
    </Scaffold>
  )
}

export default Address
